import { jsx as _jsx } from "react/jsx-runtime";
// KTK-209 + KTK-211 — Sandboxed iframe host for MCP App UI resources.
//
// The runtime's MCP host (KTK-209) extracts UI resources from tool
// responses and forwards them via Hub WSS as `agent_app_ui` frames.
// This component renders one such resource as a sandboxed iframe per
// the MCP Apps spec (`modelcontextprotocol/ext-apps`):
//
//   - sandbox attribute restricts capabilities (no top navigation, no
//     same-origin, no popups, etc.). We allow scripts so the embedded
//     UI is interactive.
//   - postMessage round-trip lets the iframe call back into the chat
//     surface; we forward the call to the runtime via the supplied
//     callback (which goes through the relay to the MCP server, then
//     the result returns the same path).
//
// Iframe sizing: starts at a reasonable default and listens for
// {type: "mcp.app/resize", height: number} from the iframe to grow.
import { useCallback, useEffect, useRef, useState } from "react";
export function AppUiIframe({ uri, serverName, toolName, onInvoke, initialHeight = 240 }) {
    const ref = useRef(null);
    const [height, setHeight] = useState(initialHeight);
    const handleMessage = useCallback(async (e) => {
        // Only accept messages from our iframe — without this, any
        // window.postMessage on the page could spoof a tool call.
        if (!ref.current || e.source !== ref.current.contentWindow)
            return;
        const data = e.data;
        if (!data || typeof data !== "object")
            return;
        if (data.type === "mcp.app/resize") {
            const h = data.height;
            if (typeof h === "number" && h > 0 && h < 4000)
                setHeight(h);
            return;
        }
        if (data.type === "mcp.app/invoke") {
            const payload = data;
            const result = await onInvoke({
                serverName,
                toolName: payload.tool,
                args: payload.args,
                callId: payload.callId,
            });
            ref.current.contentWindow?.postMessage({ type: "mcp.app/result", ...result }, "*");
        }
    }, [onInvoke, serverName]);
    useEffect(() => {
        window.addEventListener("message", handleMessage);
        return () => window.removeEventListener("message", handleMessage);
    }, [handleMessage]);
    return (_jsx("iframe", { ref: ref, src: uri, title: `${serverName}/${toolName}`, 
        // Per MCP Apps spec — restricted sandbox. allow-scripts is required
        // for the iframe to render interactive UI. Note the absence of
        // allow-same-origin: that keeps the iframe from accessing the
        // host's cookies / localStorage / DOM.
        sandbox: "allow-scripts allow-forms allow-popups-to-escape-sandbox", style: {
            width: "100%",
            height,
            border: "1px solid var(--hairline)",
            borderRadius: "var(--radius-sm)",
            background: "var(--paper-2)",
        } }));
}
//# sourceMappingURL=AppUiIframe.js.map